// hooks/useCart.js
// Cart Hook - Synchronized with Mobile App
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { apiService } from "../services/api";
import { useAuthStore } from "../stores/authStore";
import { useCartStore } from "../stores/cartStore";
import useNotificationStore from "../stores/notificationStore";

export const useCart = () => {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const syncCartRef = useRef(useCartStore.getState().syncCart);
  const lastSyncedRef = useRef(null);
  const showNotification = useNotificationStore((state) => state.showNotification);

  // Update ref when store changes (but don't trigger re-render)
  useEffect(() => {
    syncCartRef.current = useCartStore.getState().syncCart;
  }, []);

  // Fetch cart data - only when authenticated
  const {
    data: cartData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["cart"],
    queryFn: async () => {
      try {
        const response = await apiService.getCart();
        if (response.status === "success" && response.data) {
          return response.data;
        }
        return { items: [], total: 0 };
      } catch (error) {
        // Don't log errors if user is not authenticated - this is expected
        if (isAuthenticated) {
          console.error("Cart fetch error:", error);
        }
        // Return empty cart instead of throwing when not authenticated
        if (!isAuthenticated) {
          return { items: [], total: 0 };
        }
        throw error;
      }
    },
    enabled: !!isAuthenticated,
    staleTime: 1000 * 30, // 30 seconds - cart needs to be fairly fresh
    gcTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: true,
    retry: 1,
  });

  // Sync cart data to Zustand store when data changes (avoid calling in queryFn)
  useEffect(() => {
    if (cartData && JSON.stringify(cartData) !== JSON.stringify(lastSyncedRef.current)) {
      lastSyncedRef.current = cartData;
      syncCartRef.current(cartData);
    }
  }, [cartData]);

  // Helper to extract error message from various error formats
  const getErrorMessage = (error) => {
    // Handle API response errors (axios-like format)
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }
    // Handle direct error message
    if (error?.message) {
      return error.message;
    }
    // Handle string errors
    if (typeof error === 'string') {
      return error;
    }
    // Handle error object with status and message
    if (error?.status === 'error' && error?.message) {
      return error.message;
    }
    // Fallback
    return 'An error occurred. Please try again.';
  };

  // Add item to cart
  const addToCartMutation = useMutation({
    mutationFn: ({ productId, quantity = 1, sku = null, variantId = null, size = null, color = null }) =>
      apiService.addToCart(productId, quantity, { sku, variantId, size, color }),
    onSuccess: () => {
      if (isAuthenticated) {
        queryClient.invalidateQueries({ queryKey: ["cart"] });
      }
      showNotification({
        type: 'success',
        title: 'Added to Cart',
        message: 'Item has been added to your cart',
        duration: 3000,
      });
    },
    onError: (error) => {
      const message = getErrorMessage(error);
      showNotification({
        type: 'error',
        title: 'Failed to Add Item',
        message: message,
        duration: 4000,
      });
    },
  });

  // Increase item quantity
  const increaseQuantityMutation = useMutation({
    mutationFn: (itemId) => apiService.increaseCartItem(itemId),
    onSuccess: () => {
      if (isAuthenticated) {
        queryClient.invalidateQueries({ queryKey: ["cart"] });
      }
    },
    onError: (error) => {
      const message = getErrorMessage(error);
      // Check if it's a stock error
      if (message.toLowerCase().includes('stock') || message.toLowerCase().includes('available')) {
        showNotification({
          type: 'error',
          title: 'Insufficient Stock',
          message: message,
          duration: 4000,
        });
      } else {
        showNotification({
          type: 'error',
          title: 'Failed to Update Quantity',
          message: message,
          duration: 4000,
        });
      }
    },
  });

  // Decrease item quantity
  const decreaseQuantityMutation = useMutation({
    mutationFn: (itemId) => apiService.decreaseCartItem(itemId),
    onSuccess: () => {
      if (isAuthenticated) {
        queryClient.invalidateQueries({ queryKey: ["cart"] });
      }
    },
    onError: (error) => {
      const message = getErrorMessage(error);
      showNotification({
        type: 'error',
        title: 'Failed to Update Quantity',
        message: message,
        duration: 4000,
      });
    },
  });

  // Update cart item
  const updateCartItemMutation = useMutation({
    mutationFn: ({ itemId, updates }) =>
      apiService.updateCartItem(itemId, updates),
    onSuccess: () => {
      if (isAuthenticated) {
        queryClient.invalidateQueries({ queryKey: ["cart"] });
      }
    },
    onError: (error) => {
      const message = getErrorMessage(error);
      showNotification({
        type: 'error',
        title: 'Failed to Update Item',
        message: message,
        duration: 4000,
      });
    },
  });

  // Remove item from cart
  const removeItemMutation = useMutation({
    mutationFn: (itemId) => apiService.removeCartItem(itemId),
    onSuccess: () => {
      if (isAuthenticated) {
        queryClient.invalidateQueries({ queryKey: ["cart"] });
      }
      showNotification({
        type: 'success',
        title: 'Item Removed',
        message: 'Item has been removed from your cart',
        duration: 3000,
      });
    },
    onError: (error) => {
      const message = getErrorMessage(error);
      showNotification({
        type: 'error',
        title: 'Failed to Remove Item',
        message: message,
        duration: 4000,
      });
    },
  });

  // Clear entire cart
  const clearCartMutation = useMutation({
    mutationFn: () => apiService.clearCart(),
    onSuccess: () => {
      if (isAuthenticated) {
        queryClient.invalidateQueries({ queryKey: ["cart"] });
      }
    },
  });

  // Add bid to cart
  const addBidToCartMutation = useMutation({
    mutationFn: ({ bidId, size, color }) =>
      apiService.addBidToCart(bidId, size, color),
    onSuccess: () => {
      if (isAuthenticated) {
        queryClient.invalidateQueries({ queryKey: ["cart"] });
      }
      showNotification({
        type: 'success',
        title: 'Bid Added to Cart',
        message: 'Your accepted bid has been added to your cart',
        duration: 3000,
      });
    },
    onError: (error) => {
      const message = getErrorMessage(error);
      showNotification({
        type: 'error',
        title: 'Failed to Add Bid',
        message: message,
        duration: 4000,
      });
    },
  });

  // Calculate totals
  const items = cartData?.items || [];
  // Count distinct items (not total quantity)
  const totalItems = items.length;
  const totalPrice =
    Math.round(
      (cartData?.total ||
        cartData?.totalPrice ||
        items.reduce((sum, item) => {
          // IMPORTANT: Use item.price first (contains bid price if it's a bid), fallback to product price
          const itemPrice =
            item.price ||
            item.product?.price ||
            item.product?.variants?.[0]?.price ||
            0;
          return sum + itemPrice * item.quantity;
        }, 0)) *
        100
    ) / 100;

  return {
    // Data
    items,
    totalItems,
    totalPrice,
    cartData,

    // States
    isLoading,
    isError,
    error,

    // Mutations
    addToCart: addToCartMutation.mutateAsync,
    increaseQuantity: increaseQuantityMutation.mutate,
    decreaseQuantity: decreaseQuantityMutation.mutate,
    updateCartItem: updateCartItemMutation.mutate,
    removeItem: removeItemMutation.mutate,
    clearCart: clearCartMutation.mutate,
    clearCartAsync: clearCartMutation.mutateAsync,
    addBidToCart: addBidToCartMutation.mutate,

    // Manual refetch
    refetch,

    // Mutation states
    isAddingToCart: addToCartMutation.isPending,
    isUpdating:
      updateCartItemMutation.isPending ||
      increaseQuantityMutation.isPending ||
      decreaseQuantityMutation.isPending,
    isRemoving: removeItemMutation.isPending,
    isClearing: clearCartMutation.isPending,
  };
};

export default useCart;

